import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useTheme } from "../context/ThemeContext";
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  Users,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Sun,
  Moon,
  Waves,
  Flame,
  Receipt,
  PanelLeftClose,
  PanelLeft,
  Shield,
  Banknote,
  UserCog,
  Activity,
  BarChart3,
  Award,
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { ScrollArea } from "./ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import Notifications from "./Notifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const hasPermission = (user, module, action = "view") => {
  return user?.permissions?.[module]?.[action] === true;
};

const navigationGroups = [
  {
    type: "link",
    name: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    roles: ["Admin One", "Engineer", "Accountant", "HR"],
    module: "dashboard",
  },
  {
    type: "link",
    name: "Projects",
    href: "/projects",
    icon: FolderKanban,
    roles: ["Admin One", "Engineer"],
    module: "projects",
  },
  {
    type: "group",
    name: "Financials",
    icon: Banknote,
    roles: ["Admin One", "Accountant"],
    items: [
      { name: "Billing List",     href: "/billing",          icon: Receipt,   roles: ["Admin One", "Accountant"], module: "billing" },
      { name: "Project Expenses", href: "/project-expenses", icon: BarChart3, roles: ["Admin One", "Accountant"], module: "expenses" },
      { name: "Admin Expenses",   href: "/admin-expenses",   icon: Banknote,  roles: ["Admin One", "Accountant"], module: "expenses" },
    ],
  },
  {
    type: "group",
    name: "Administration",
    icon: UserCog,
    roles: ["Admin One"],
    items: [
      { name: "Users", href: "/users", icon: Users, roles: ["Admin One"], module: "users" },
      { name: "Roles", href: "/roles", icon: Shield, roles: ["Admin One"], module: "roles" },
      { name: "Audit Logs", href: "/audit-logs", icon: Activity, roles: ["Admin One"], module: "audit_logs" },
      { name: "Licenses", href: "/licenses", icon: Award, roles: ["Admin One"] },
    ],
  },
  {
    type: "link",
    name: "Reports",
    href: "/reports",
    icon: FileText,
    roles: ["Admin One"],
    module: "reports",
  },
];

function LiveDateTime() {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const date = now.toLocaleDateString("en-PH", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const time = now.toLocaleTimeString("en-PH", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  return (
    <span className="text-xs text-muted-foreground font-mono hidden md:flex items-center gap-2">
      <span>{date}</span>
      <span className="text-foreground font-medium">{time}</span>
    </span>
  );
}

export default function Layout({ children, user }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });
  const [openGroups, setOpenGroups] = useState(() => {
    const saved = localStorage.getItem("sidebarOpenGroups");
    return saved ? JSON.parse(saved) : ["Financials", "Administration"];
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, cycleTheme } = useTheme();

  const filterByRole = (items) => {
    return items.filter(item => {
      const isSuperAdmin = user?.role === "SuperAdmin";
      const hasRole = isSuperAdmin || item.roles?.includes(user?.role);
      if (!hasRole) return false;
      if (!isSuperAdmin && item.module) {
        const modulePerms = user?.permissions?.[item.module];
        if (modulePerms !== undefined) {
          return modulePerms?.view === true;
        }
      }
      return true;
    });
  };

  const filteredNavigation = navigationGroups
    .filter(group => {
      const isSuperAdmin = user?.role === "SuperAdmin";
      const hasRole = isSuperAdmin || group.roles?.includes(user?.role);
      if (!hasRole) return false;
      if (!isSuperAdmin && group.type === "link" && group.module) {
        const modulePerms = user?.permissions?.[group.module];
        if (modulePerms !== undefined) {
          return modulePerms?.view === true;
        }
      }
      return true;
    })
    .map(group => {
      if (group.type === "group") {
        return {
          ...group,
          items: filterByRole(group.items || []),
        };
      }
      return group;
    })
    .filter(group => group.type === "link" || (group.items && group.items.length > 0));

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem("sidebarOpenGroups", JSON.stringify(openGroups));
  }, [openGroups]);

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleGroup = (groupName) => {
    setOpenGroups(prev =>
      prev.includes(groupName)
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success("Logged out successfully");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login");
    }
  };

  const isActive = (href) => {
    if (href === "/dashboard") return location.pathname === "/dashboard";
    if (href === "/billing") return location.pathname === "/billing";
    if (href === "/add-billing") return location.pathname === "/add-billing";
    return location.pathname.startsWith(href);
  };

  const isGroupActive = (group) => {
    if (group.type === "link") return isActive(group.href);
    return group.items?.some(item => isActive(item.href));
  };

  const renderNavItem = (item, isNested = false) => {
    const Icon = item.icon;
    const active = isActive(item.href);

    return (
      <Link
        key={item.name}
        to={item.href}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
        title={sidebarCollapsed ? item.name : undefined}
        className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
          active
            ? "bg-muted text-foreground border-l-2 border-[hsl(var(--ring))] -ml-[2px] pl-[14px]"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        } ${isNested && !sidebarCollapsed ? "ml-4" : ""} ${sidebarCollapsed ? "lg:justify-center lg:px-2" : ""}`}
        onClick={() => setSidebarOpen(false)}
      >
        <Icon className={`w-4 h-4 flex-shrink-0 ${active ? "text-[hsl(var(--ring))]" : ""}`} />
        <span className={`${sidebarCollapsed ? "lg:hidden" : ""}`}>{item.name}</span>
        {active && !sidebarCollapsed && <ChevronRight className="w-4 h-4 ml-auto hidden lg:block" />}
      </Link>
    );
  };

  const renderNavGroup = (group) => {
    const Icon = group.icon;
    const groupActive = isGroupActive(group);
    const isOpen = openGroups.includes(group.name);

    if (sidebarCollapsed) {
      return (
        <div key={group.name} className="space-y-1">
          <div
            title={group.name}
            className={`flex items-center justify-center px-2 py-2 rounded-sm text-sm transition-colors ${
              groupActive
                ? "text-foreground bg-muted/50"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
          >
            <Icon className={`w-4 h-4 ${groupActive ? "text-[hsl(var(--ring))]" : ""}`} />
          </div>
        </div>
      );
    }

    return (
      <Collapsible
        key={group.name}
        open={isOpen}
        onOpenChange={() => toggleGroup(group.name)}
      >
        <CollapsibleTrigger asChild>
          <button
            data-testid={`nav-group-${group.name.toLowerCase().replace(/\s+/g, "-")}`}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
              groupActive
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
          >
            <Icon className={`w-4 h-4 flex-shrink-0 ${groupActive ? "text-[hsl(var(--ring))]" : ""}`} />
            <span className="flex-1 text-left">{group.name}</span>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-1 mt-1">
          {group.items?.map(item => renderNavItem(item, true))}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <div className="min-h-screen bg-background grid-pattern">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/80 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-card border-r border-border transform transition-all duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } ${sidebarCollapsed ? "lg:w-16" : "lg:w-64"} w-64`}
      >
        <div className="flex flex-col h-full">
          <div className="p-4 lg:p-6 border-b border-border">
            <Link to={filteredNavigation.length > 0 ? filteredNavigation[0].href || "/dashboard" : "/login"} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[hsl(var(--secondary))] rounded-sm flex items-center justify-center flex-shrink-0">
                <span className="text-white font-heading font-bold text-lg">S</span>
              </div>
              <div className={`${sidebarCollapsed ? "lg:hidden" : ""} transition-opacity duration-200`}>
                <h1 className="font-heading font-bold text-lg text-foreground tracking-tight">
                  StreamCon
                </h1>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                  Efficiency in Every Project
                </p>
              </div>
            </Link>
          </div>

          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-3">
              {filteredNavigation.map((item) => {
                if (item.type === "group") {
                  return renderNavGroup(item);
                }
                return renderNavItem(item);
              })}
            </nav>
          </ScrollArea>

          <div className="hidden lg:block px-3 py-2 border-t border-border">
            <Button
              data-testid="sidebar-collapse-toggle"
              variant="ghost"
              size="sm"
              onClick={toggleSidebarCollapse}
              className={`w-full flex items-center gap-2 text-muted-foreground hover:text-foreground ${sidebarCollapsed ? "justify-center" : "justify-start"}`}
            >
              {sidebarCollapsed ? (
                <PanelLeft className="w-4 h-4" />
              ) : (
                <>
                  <PanelLeftClose className="w-4 h-4" />
                  <span>Collapse</span>
                </>
              )}
            </Button>
          </div>

          <div className="p-4 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className={`flex items-center gap-3 w-full p-2 rounded-sm hover:bg-muted/50 transition-colors ${sidebarCollapsed ? "lg:justify-center" : ""}`}
                >
                  <Avatar className="h-8 w-8 flex-shrink-0">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-[hsl(var(--secondary))] text-white text-xs">
                      {user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className={`flex-1 text-left ${sidebarCollapsed ? "lg:hidden" : ""}`}>
                    <p className="text-sm font-medium text-foreground truncate">
                      {user?.name || "User"}
                    </p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                      {user?.role || "HR"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem disabled>
                  <span className="text-muted-foreground">{user?.email}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="text-red-500 focus:text-red-500"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      <div className={`transition-all duration-200 ${sidebarCollapsed ? "lg:pl-16" : "lg:pl-64"}`}>
        <header className="sticky top-0 z-30 bg-card/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center justify-between h-14 px-4 lg:px-6">
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-muted-foreground hover:text-foreground"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            <div className="hidden lg:block"></div>

            <div className="flex items-center gap-2">
              <Notifications />

              {/* Theme Toggle — cycles: Dark → Light → Ocean → Sunset */}
              <Button
                data-testid="theme-toggle"
                variant="ghost"
                size="icon"
                onClick={cycleTheme}
                className="h-9 w-9"
                title={`Theme: ${theme} — click to cycle`}
              >
                {theme === "dark" && <Moon className="w-4 h-4 text-red-500" />}
                {theme === "light" && <Sun className="w-4 h-4 text-blue-500" />}
                {theme === "ocean" && <Waves className="w-4 h-4 text-blue-400" />}
                {theme === "sunset" && <Flame className="w-4 h-4 text-orange-500" />}
              </Button>

              <LiveDateTime />
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}